import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../icons';

const TOPIC_CENTERS = {
  OpenCodex: [.5, .23],
  'Codex fleet': [.74, .34],
  'Media AI': [.79, .69],
  'Web & product': [.24, .34],
  'Cloud & infra': [.22, .7],
  'CopyFig & dat.city': [.49, .8],
  Research: [.46, .49],
  Workflow: [.61, .58],
  Other: [.36, .62],
};
const KIND_COLORS = { group: '#8fa783', rollout: '#e0aa46', task: '#a7b0b4', skill: '#f2ede2' };
const HOST_PALETTE = ['#83d6af', '#68b8dc', '#e0aa46', '#aa93d6', '#ff786b', '#d6c083'];
const KIND_RADII = { group: 7.2, rollout: 5.2, task: 3.7, skill: 4.8 };
const MIN_ZOOM = .12;
const MAX_ZOOM = 4.25;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function hash(value) {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) result = Math.imul(result ^ value.charCodeAt(index), 16777619);
  return result >>> 0;
}

function shortTitle(value, max = 32) {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function hostColor(hostId = 'local') {
  if (hostId === 'local') return HOST_PALETTE[0];
  return HOST_PALETTE[1 + (hash(hostId) % (HOST_PALETTE.length - 1))];
}

function topicPoint(topic, width, height) {
  const [x, y] = TOPIC_CENTERS[topic] ?? TOPIC_CENTERS.Other;
  return { x: x * width, y: y * height };
}

function nodeAnchor(node, model) {
  const island = model.hostIslands.get(node.hostId) ?? model.defaultIsland;
  const headerSpace = model.hostIslands.size ? 105 : 0;
  const localPoint = topicPoint(node.topic, island.width, island.height - headerSpace);
  return { x: island.x + localPoint.x, y: island.y + headerSpace + localPoint.y };
}

function buildModel(sourceNodes, sourceEdges, width, height, previous) {
  const hostIds = [...new Set(sourceNodes.map((node) => node.hostId).filter(Boolean))];
  const hostCount = hostIds.length;
  const combined = hostCount > 1;
  const hostCounts = new Map(hostIds.map((hostId) => [hostId, sourceNodes.filter((node) => node.hostId === hostId).length]));
  const islandWidth = width * 1.08;
  const islandGap = Math.max(320, width * .3);
  const islandHeight = height * clamp(Math.sqrt(Math.max(...hostCounts.values(), sourceNodes.length) / 250), 1, 1.55);
  const hostIslands = new Map();
  if (combined) {
    hostIds.forEach((hostId, index) => hostIslands.set(hostId, { id: hostId, x: index * (islandWidth + islandGap), y: 0, width: islandWidth, height: islandHeight, count: hostCounts.get(hostId) }));
  }
  const layoutScale = combined ? 1 : clamp(Math.sqrt(sourceNodes.length / 250), 1, 2.65);
  const layoutWidth = combined ? hostIds.length * islandWidth + Math.max(0, hostIds.length - 1) * islandGap : width * layoutScale;
  const layoutHeight = combined ? islandHeight : height * layoutScale;
  const defaultIsland = { id: 'default', x: 0, y: 0, width: layoutWidth, height: layoutHeight, count: sourceNodes.length };
  const previousNodes = previous?.nodeMap ?? new Map();
  const previousWidth = previous?.layoutWidth || layoutWidth;
  const previousHeight = previous?.layoutHeight || layoutHeight;
  const counts = new Map();
  const nodes = sourceNodes.map((source, index) => {
    const seed = hash(source.id);
    const prior = previousNodes.get(source.id);
    const center = nodeAnchor(source, { hostIslands, defaultIsland });
    const angle = ((seed % 1009) / 1009) * Math.PI * 2 + index * .23;
    const spread = 22 + ((seed >>> 8) % 80);
    return {
      ...source,
      x: prior ? prior.x * (layoutWidth / previousWidth) : center.x + Math.cos(angle) * spread * layoutScale,
      y: prior ? prior.y * (layoutHeight / previousHeight) : center.y + Math.sin(angle) * spread * layoutScale,
      vx: prior?.vx ?? 0,
      vy: prior?.vy ?? 0,
      radius: KIND_RADII[source.kind] ?? 4,
      degree: 0,
      fx: null,
      fy: null,
      parentId: null,
      simulationIndex: index,
      focusOpacity: 1,
      focusFromOpacity: 1,
    };
  });
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const links = sourceEdges.flatMap((edge) => {
    const source = nodeMap.get(edge.source);
    const target = nodeMap.get(edge.target);
    if (!source || !target) return [];
    source.degree += 1;
    target.degree += 1;
    counts.set(source.id, (counts.get(source.id) ?? 0) + 1);
    counts.set(target.id, (counts.get(target.id) ?? 0) + 1);
    return [{ ...edge, source, target, focusOpacity: 1, focusFromOpacity: 1 }];
  });
  const preferredEvidence = new Map();
  links.filter((link) => link.type === 'evidence').forEach((link) => {
    const current = preferredEvidence.get(link.target.id);
    if (!current || (link.source.kind === 'task' && current.source.kind !== 'task')) preferredEvidence.set(link.target.id, link);
  });
  links.forEach((link) => {
    link.backbone = link.type !== 'evidence' || preferredEvidence.get(link.target.id) === link;
    if (link.type === 'contains') link.target.parentId = link.source.id;
  });

  const groupsByTopic = new Map();
  nodes.filter((node) => node.kind === 'group').forEach((node) => {
    const key = `${node.hostId || 'default'}::${node.topic}`;
    const family = groupsByTopic.get(key) ?? [];
    family.push(node);
    groupsByTopic.set(key, family);
  });
  groupsByTopic.forEach((family) => family.forEach((node, index) => {
    if (previousNodes.has(node.id)) return;
    const center = nodeAnchor(node, { hostIslands, defaultIsland });
    const seed = hash(node.id);
    const angle = index * 2.399963 + ((seed % 71) / 71) * .42;
    const radius = (22 + Math.sqrt(index) * 34) * layoutScale;
    node.x = center.x + Math.cos(angle) * radius;
    node.y = center.y + Math.sin(angle) * radius;
  }));
  nodes.filter((node) => node.kind === 'task' && !previousNodes.has(node.id)).forEach((node) => {
    const parent = nodeMap.get(node.parentId);
    if (!parent) return;
    const seed = hash(node.id);
    const angle = ((seed % 997) / 997) * Math.PI * 2;
    const radius = 30 + ((seed >>> 10) % 25);
    node.x = parent.x + Math.cos(angle) * radius;
    node.y = parent.y + Math.sin(angle) * radius;
  });
  nodes.filter((node) => node.kind === 'rollout' && !previousNodes.has(node.id)).forEach((node) => {
    const sources = links.filter((link) => link.type === 'evidence' && link.target.id === node.id).map((link) => link.source);
    if (!sources.length) return;
    const anchor = sources.reduce((point, source) => ({ x: point.x + source.x / sources.length, y: point.y + source.y / sources.length }), { x: 0, y: 0 });
    const seed = hash(node.id);
    const angle = ((seed % 991) / 991) * Math.PI * 2;
    const radius = 38 + ((seed >>> 9) % 28);
    node.x = anchor.x + Math.cos(angle) * radius;
    node.y = anchor.y + Math.sin(angle) * radius;
  });
  const adjacency = new Map(nodes.map((node) => [node.id, new Set()]));
  links.forEach(({ source, target }) => {
    adjacency.get(source.id)?.add(target.id);
    adjacency.get(target.id)?.add(source.id);
  });
  return {
    nodes,
    links,
    nodeMap,
    adjacency,
    width,
    height,
    layoutWidth,
    layoutHeight,
    layoutScale,
    hostCount,
    hostIslands,
    defaultIsland,
    islandGap,
    alpha: 1,
    reveal: performance.now(),
    transformTween: null,
    inertia: { x: 0, y: 0 },
    focusId: null,
    focusStartedAt: 0,
  };
}

function simulate(model) {
  const { nodes, links } = model;
  const alpha = model.alpha;
  const cellSize = 92;
  const grid = new Map();
  nodes.forEach((node) => {
    const key = `${Math.floor(node.x / cellSize)}:${Math.floor(node.y / cellSize)}`;
    const cell = grid.get(key) ?? [];
    cell.push(node);
    grid.set(key, cell);
  });
  nodes.forEach((a) => {
    const cellX = Math.floor(a.x / cellSize);
    const cellY = Math.floor(a.y / cellSize);
    for (let offsetX = -2; offsetX <= 2; offsetX += 1) {
      for (let offsetY = -2; offsetY <= 2; offsetY += 1) {
        const nearby = grid.get(`${cellX + offsetX}:${cellY + offsetY}`) ?? [];
        nearby.forEach((b) => {
          if (b.simulationIndex <= a.simulationIndex) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distanceSquared = dx * dx + dy * dy + .01;
      if (distanceSquared > 34000) return;
      const distance = Math.sqrt(distanceSquared);
      const minDistance = a.radius + b.radius + 5;
      const charge = Math.min(.72, (80 * alpha) / distanceSquared);
      const collision = distance < minDistance ? ((minDistance - distance) / distance) * .12 : 0;
      const force = charge + collision;
      const fx = dx * force;
      const fy = dy * force;
      if (a.fx == null) { a.vx -= fx; a.vy -= fy; }
      if (b.fx == null) { b.vx += fx; b.vy += fy; }
        });
      }
    }
  });

  links.forEach((link) => {
    const dx = link.target.x - link.source.x;
    const dy = link.target.y - link.source.y;
    const distance = Math.sqrt(dx * dx + dy * dy) || 1;
    const ideal = link.type === 'related' ? 128 : link.type === 'evidence' ? 82 : 62;
    const strength = link.type === 'related' ? .0032 : .0085;
    const force = ((distance - ideal) / distance) * strength * alpha;
    const fx = dx * force;
    const fy = dy * force;
    if (link.source.fx == null) { link.source.vx += fx; link.source.vy += fy; }
    if (link.target.fx == null) { link.target.vx -= fx; link.target.vy -= fy; }
  });

  nodes.forEach((node) => {
    const center = nodeAnchor(node, model);
    const parent = node.parentId ? model.nodeMap.get(node.parentId) : null;
    const anchor = parent ?? center;
    const clusterStrength = node.kind === 'group' ? .001 : node.kind === 'task' ? .00045 : .0007;
    if (node.fx == null) {
      node.vx += (anchor.x - node.x) * clusterStrength * alpha;
      node.vy += (anchor.y - node.y) * clusterStrength * alpha;
      node.vx *= .86;
      node.vy *= .86;
      node.x += node.vx;
      node.y += node.vy;
    } else {
      node.x = node.fx;
      node.y = node.fy;
      node.vx = 0;
      node.vy = 0;
    }
  });
  model.alpha *= .978;
}

function graphBounds(nodes, includeHostHeaders = false) {
  if (!nodes.length) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  const bounds = nodes.reduce((result, node) => ({
    minX: Math.min(result.minX, node.x - node.radius),
    minY: Math.min(result.minY, node.y - node.radius),
    maxX: Math.max(result.maxX, node.x + node.radius),
    maxY: Math.max(result.maxY, node.y + node.radius),
  }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
  if (includeHostHeaders && nodes.some((node) => node.hostId)) bounds.minY -= 88;
  return bounds;
}

function graphLayoutBounds(model) {
  if (model.hostIslands.size) {
    const islands = [...model.hostIslands.values()];
    return {
      minX: Math.min(...islands.map((island) => island.x)),
      minY: Math.min(...islands.map((island) => island.y)),
      maxX: Math.max(...islands.map((island) => island.x + island.width)),
      maxY: Math.max(...islands.map((island) => island.y + island.height)),
    };
  }
  return {
    minX: model.defaultIsland.x,
    minY: model.defaultIsland.y,
    maxX: model.defaultIsland.x + model.defaultIsland.width,
    maxY: model.defaultIsland.y + model.defaultIsland.height,
  };
}

function fitTransform(model) {
  const layoutBounds = graphLayoutBounds(model);
  const liveBounds = graphBounds(model.nodes, model.hostCount > 1);
  const bounds = {
    minX: Math.min(layoutBounds.minX, liveBounds.minX),
    minY: Math.min(layoutBounds.minY, liveBounds.minY),
    maxX: Math.max(layoutBounds.maxX, liveBounds.maxX),
    maxY: Math.max(layoutBounds.maxY, liveBounds.maxY),
  };
  const graphWidth = Math.max(1, bounds.maxX - bounds.minX);
  const graphHeight = Math.max(1, bounds.maxY - bounds.minY);
  const shortSide = Math.min(model.width, model.height);
  const horizontalInset = clamp(shortSide * .1, 54, 88);
  const topInset = clamp(shortSide * .11, 64, 92);
  const bottomInset = clamp(shortSide * .13, 76, 108);
  const innerWidth = Math.max(1, model.width - horizontalInset * 2);
  const innerHeight = Math.max(1, model.height - topInset - bottomInset);
  const k = clamp(Math.min(innerWidth / graphWidth, innerHeight / graphHeight), MIN_ZOOM, 1.12);
  return {
    k,
    x: horizontalInset + innerWidth / 2 - ((bounds.minX + bounds.maxX) / 2) * k,
    y: topInset + innerHeight / 2 - ((bounds.minY + bounds.maxY) / 2) * k,
  };
}

function updateFocusOpacity(model, focusId, connected, now) {
  if (model.focusId !== focusId) {
    model.focusId = focusId;
    model.focusStartedAt = now;
    model.nodes.forEach((node) => { node.focusFromOpacity = node.focusOpacity ?? 1; });
    model.links.forEach((link) => { link.focusFromOpacity = link.focusOpacity ?? 1; });
  }
  const progress = clamp((now - model.focusStartedAt) / 190, 0, 1);
  const eased = 1 - (1 - progress) ** 3;
  model.nodes.forEach((node) => {
    const target = !focusId || connected?.has(node.id) ? 1 : .14;
    node.focusOpacity = node.focusFromOpacity + (target - node.focusFromOpacity) * eased;
  });
  model.links.forEach((link) => {
    const active = focusId && (link.source.id === focusId || link.target.id === focusId);
    const target = !focusId || active ? 1 : .22;
    link.focusOpacity = link.focusFromOpacity + (target - link.focusFromOpacity) * eased;
  });
  return progress < 1;
}

function drawRoundedLabel(context, text, x, y, color) {
  context.font = '600 12px Inter, ui-sans-serif, sans-serif';
  const width = context.measureText(text).width + 16;
  const left = x - width / 2;
  const top = y - 31;
  context.fillStyle = 'rgba(8, 11, 12, .94)';
  context.strokeStyle = 'rgba(145, 157, 155, .48)';
  context.lineWidth = 1;
  context.beginPath();
  context.roundRect(left, top, width, 24, 5);
  context.fill();
  context.stroke();
  context.fillStyle = color;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, x, top + 12);
}

function drawGraph(canvas, model, transform, selectedId, hoveredId, showLabels, linkMode, timelineIds, now) {
  const context = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, model.width, model.height);
  const reveal = clamp((now - model.reveal) / 440, 0, 1);
  const nodeIsVisible = (node) => !timelineIds || timelineIds.has(node.id);
  const focusId = model.nodeMap.get(hoveredId || selectedId) && nodeIsVisible(model.nodeMap.get(hoveredId || selectedId)) ? hoveredId || selectedId : null;
  const connected = focusId ? new Set([focusId, ...[...(model.adjacency.get(focusId) ?? [])].filter((id) => nodeIsVisible(model.nodeMap.get(id)))]) : null;
  const focusAnimating = updateFocusOpacity(model, focusId, connected, now);
  canvas.dataset.focusTransition = focusAnimating ? 'active' : 'settled';
  canvas.dataset.focusedNode = focusId ?? '';

  if (timelineIds) {
    const previous = model.timelineIds ?? new Set();
    timelineIds.forEach((id) => {
      if (!previous.has(id)) {
        const node = model.nodeMap.get(id);
        if (node) node.enteredAt = now;
      }
    });
    model.timelineIds = timelineIds;
  } else {
    model.timelineIds = null;
  }

  context.save();
  context.translate(transform.x, transform.y);
  context.scale(transform.k, transform.k);

  const islands = model.hostIslands.size ? [...model.hostIslands.values()] : [model.defaultIsland];
  islands.forEach((island, islandIndex) => {
    const islandNodes = model.nodes.filter((node) => (node.hostId ?? 'default') === island.id && nodeIsVisible(node));
    if (!islandNodes.length && island.id !== 'default') return;
    if (model.hostIslands.size) {
      const label = islandNodes[0]?.hostLabel ?? island.id;
      const headingX = island.x + 25;
      const headingY = island.y + 18;
      context.font = `400 ${Math.max(28, 38 / transform.k)}px Iowan Old Style, Georgia, serif`;
      context.textAlign = 'left';
      context.textBaseline = 'top';
      context.fillStyle = hostColor(island.id);
      context.fillText(label, headingX, headingY);
      if (transform.k >= .42) {
        context.font = `${Math.max(7, 9 / transform.k)}px "SFMono-Regular", monospace`;
        context.fillStyle = 'rgba(193, 204, 199, .58)';
        context.fillText(`${island.count} MEMORY NODES`, headingX + 2 / transform.k, headingY + 41 / transform.k);
      }
      if (islandIndex < islands.length - 1) {
        context.strokeStyle = 'rgba(122, 137, 133, .16)';
        context.lineWidth = 1 / transform.k;
        context.beginPath();
        context.moveTo(island.x + island.width + model.islandGap / 2, island.y + 40);
        context.lineTo(island.x + island.width + model.islandGap / 2, island.y + island.height - 40);
        context.stroke();
      }
    }
    if (model.hostIslands.size && transform.k < .42) return;
    Object.entries(TOPIC_CENTERS).forEach(([topic]) => {
      if (!islandNodes.some((node) => node.topic === topic)) return;
      const point = nodeAnchor({ topic, hostId: island.id }, model);
      context.font = `${Math.max(6.5, 8 / transform.k)}px "SFMono-Regular", monospace`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillStyle = focusId ? 'rgba(226, 232, 226, .09)' : 'rgba(226, 232, 226, .17)';
      context.fillText(topic.toUpperCase(), point.x, point.y - 22);
    });
  });

  model.links.forEach((link) => {
    if (!nodeIsVisible(link.source) || !nodeIsVisible(link.target)) return;
    const active = focusId && (link.source.id === focusId || link.target.id === focusId);
    if (!active && linkMode === 'focus' && !link.backbone) return;
    context.beginPath();
    context.moveTo(link.source.x, link.source.y);
    if (link.type === 'contains') {
      context.lineTo(link.target.x, link.target.y);
    } else {
      const dx = link.target.x - link.source.x;
      const dy = link.target.y - link.source.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      const direction = (hash(link.id) & 1) ? 1 : -1;
      const bend = Math.min(34, distance * .14) * direction;
      const controlX = (link.source.x + link.target.x) / 2 - (dy / distance) * bend;
      const controlY = (link.source.y + link.target.y) / 2 + (dx / distance) * bend;
      context.quadraticCurveTo(controlX, controlY, link.target.x, link.target.y);
    }
    context.setLineDash(link.type === 'related' ? [6 / transform.k, 6 / transform.k] : []);
    context.lineWidth = (active ? 2.2 : link.type === 'related' ? 1 : 1.2) / transform.k;
    context.globalAlpha = (link.focusOpacity ?? 1) * reveal;
    context.strokeStyle = active
      ? 'rgba(255, 107, 93, .95)'
      : link.type === 'related'
        ? 'rgba(131, 214, 175, .16)'
        : link.type === 'evidence'
          ? 'rgba(224, 170, 70, .22)'
          : 'rgba(151, 166, 164, .32)';
    context.stroke();
    context.globalAlpha = 1;
  });
  context.setLineDash([]);

  model.nodes.forEach((node) => {
    if (!nodeIsVisible(node)) return;
    const active = node.id === selectedId || node.id === hoveredId;
    const entrance = node.enteredAt ? clamp((now - node.enteredAt) / 420, 0, 1) : 1;
    const easeEntrance = 1 - (1 - entrance) ** 3;
    const radius = (node.radius / Math.sqrt(transform.k)) * (.25 + easeEntrance * .75);
    if (active) {
      context.beginPath();
      context.arc(node.x, node.y, radius + 7 / transform.k, 0, Math.PI * 2);
      context.fillStyle = node.id === selectedId ? 'rgba(255, 107, 93, .15)' : 'rgba(131, 214, 175, .13)';
      context.fill();
      context.strokeStyle = node.id === selectedId ? 'rgba(255, 107, 93, .7)' : 'rgba(131, 214, 175, .65)';
      context.lineWidth = 1.2 / transform.k;
      context.stroke();
    }
    context.beginPath();
    context.arc(node.x, node.y, active ? radius * 1.22 : radius, 0, Math.PI * 2);
    context.globalAlpha = .96 * reveal * easeEntrance * (node.focusOpacity ?? 1);
    context.fillStyle = active && node.id === selectedId ? '#ff6b5d' : KIND_COLORS[node.kind];
    context.fill();
    context.strokeStyle = active ? '#f8f4ea' : node.hostId ? hostColor(node.hostId) : 'rgba(9, 12, 13, .95)';
    context.lineWidth = (active ? 1.7 : node.hostId ? 1.65 : 1.2) / transform.k;
    context.stroke();
    context.globalAlpha = 1;

  });
  context.restore();

  if (showLabels) {
    const occupied = [];
    const candidates = model.nodes
      .filter(nodeIsVisible)
      .filter((node) => !connected || connected.has(node.id))
      .filter((node) => node.id !== focusId && (
        (node.kind === 'group' && node.degree >= 2)
        || (transform.k > 1.35 && node.kind !== 'task' && node.degree >= 2)
        || transform.k > 2.2
      ))
      .sort((a, b) => (b.kind === 'group') - (a.kind === 'group') || b.degree - a.degree);
    context.font = '11px Inter, ui-sans-serif, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'top';
    candidates.forEach((node) => {
      const x = node.x * transform.k + transform.x;
      const y = node.y * transform.k + transform.y + node.radius + 6;
      if (x < 55 || x > model.width - 55 || y < 16 || y > model.height - 22) return;
      const title = shortTitle(node.title, 25);
      const width = context.measureText(title).width;
      const rect = { left: x - width / 2 - 5, right: x + width / 2 + 5, top: y - 2, bottom: y + 15 };
      if (occupied.some((item) => rect.left < item.right && rect.right > item.left && rect.top < item.bottom && rect.bottom > item.top)) return;
      occupied.push(rect);
      context.fillStyle = node.kind === 'group' ? 'rgba(235, 232, 222, .78)' : 'rgba(193, 202, 198, .68)';
      context.fillText(title, x, y);
    });
  }

  const focusedNode = model.nodeMap.get(focusId);
  if (focusedNode) {
    drawRoundedLabel(
      context,
      shortTitle(focusedNode.title, 38),
      focusedNode.x * transform.k + transform.x,
      focusedNode.y * transform.k + transform.y - focusedNode.radius - 2,
      focusedNode.id === selectedId ? '#ff8b80' : '#a9ebcc',
    );
  }
  return focusAnimating;
}

function GraphSurface({ nodes, edges, selectedId, onSelect, showLabels, linkMode, timelineIds, controlsRef }) {
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const transformRef = useRef({ x: 0, y: 0, k: 1 });
  const userTransformedRef = useRef(false);
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const hoveredRef = useRef(null);
  const selectedRef = useRef(selectedId);
  const showLabelsRef = useRef(showLabels);
  const linkModeRef = useRef(linkMode);
  const timelineIdsRef = useRef(timelineIds);
  const onSelectRef = useRef(onSelect);
  const [hoveredId, setHoveredId] = useState(null);

  selectedRef.current = selectedId;
  showLabelsRef.current = showLabels;
  linkModeRef.current = linkMode;
  timelineIdsRef.current = timelineIds;
  onSelectRef.current = onSelect;

  const runFrame = useCallback((now) => {
    const canvas = canvasRef.current;
    const model = modelRef.current;
    if (!canvas || !model) { frameRef.current = null; return; }
    let keepGoing = false;
    if (model.alpha > .012 || dragRef.current?.type === 'node') {
      simulate(model);
      keepGoing = true;
    }
    // Follow the expanding force layout only while the camera is still in its
    // untouched default state. Any deliberate pan or zoom immediately opts out.
    if (!userTransformedRef.current && !model.transformTween) transformRef.current = fitTransform(model);
    if (model.transformTween) {
      const elapsed = clamp((now - model.transformTween.started) / model.transformTween.duration, 0, 1);
      const eased = 1 - (1 - elapsed) ** 3;
      const { from, to } = model.transformTween;
      transformRef.current = {
        x: from.x + (to.x - from.x) * eased,
        y: from.y + (to.y - from.y) * eased,
        k: from.k + (to.k - from.k) * eased,
      };
      if (elapsed < 1) keepGoing = true;
      else model.transformTween = null;
    }
    if (!dragRef.current && (Math.abs(model.inertia.x) > .08 || Math.abs(model.inertia.y) > .08)) {
      transformRef.current.x += model.inertia.x;
      transformRef.current.y += model.inertia.y;
      model.inertia.x *= .9;
      model.inertia.y *= .9;
      keepGoing = true;
    }
    if (timelineIdsRef.current && model.nodes.some((node) => timelineIdsRef.current.has(node.id) && node.enteredAt && now - node.enteredAt < 440)) keepGoing = true;
    canvas.dataset.graphScale = transformRef.current.k.toFixed(4);
    canvas.dataset.cameraMode = userTransformedRef.current ? 'manual' : 'fit';
    const focusAnimating = drawGraph(canvas, model, transformRef.current, selectedRef.current, hoveredRef.current, showLabelsRef.current, linkModeRef.current, timelineIdsRef.current, now);
    const visibleNodes = timelineIdsRef.current ? model.nodes.filter((node) => timelineIdsRef.current.has(node.id)) : model.nodes;
    if (visibleNodes.length) {
      const screenBounds = graphBounds(visibleNodes);
      canvas.dataset.nodeInsetLeft = (screenBounds.minX * transformRef.current.k + transformRef.current.x).toFixed(1);
      canvas.dataset.nodeInsetRight = (model.width - (screenBounds.maxX * transformRef.current.k + transformRef.current.x)).toFixed(1);
      canvas.dataset.nodeInsetTop = (screenBounds.minY * transformRef.current.k + transformRef.current.y).toFixed(1);
      canvas.dataset.nodeInsetBottom = (model.height - (screenBounds.maxY * transformRef.current.k + transformRef.current.y)).toFixed(1);
    }
    if (focusAnimating) keepGoing = true;
    if (keepGoing) frameRef.current = requestAnimationFrame(runFrame);
    else frameRef.current = null;
  }, []);

  const requestDraw = useCallback(() => {
    if (frameRef.current == null) frameRef.current = requestAnimationFrame(runFrame);
  }, [runFrame]);

  const animateTransform = useCallback((target, duration = 300) => {
    const model = modelRef.current;
    if (!model) return;
    model.transformTween = { from: { ...transformRef.current }, to: target, started: performance.now(), duration };
    model.inertia = { x: 0, y: 0 };
    requestDraw();
  }, [requestDraw]);

  const fit = useCallback(() => {
    const model = modelRef.current;
    if (model) {
      userTransformedRef.current = false;
      animateTransform(fitTransform(model), 360);
    }
  }, [animateTransform]);

  const zoomBy = useCallback((factor) => {
    const model = modelRef.current;
    if (!model) return;
    userTransformedRef.current = true;
    const current = transformRef.current;
    const k = clamp(current.k * factor, MIN_ZOOM, MAX_ZOOM);
    animateTransform({
      k,
      x: model.width / 2 - ((model.width / 2 - current.x) / current.k) * k,
      y: model.height / 2 - ((model.height / 2 - current.y) / current.k) * k,
    }, 220);
  }, [animateTransform]);

  const reheat = useCallback(() => {
    const model = modelRef.current;
    if (!model) return;
    model.alpha = .72;
    model.nodes.forEach((node) => { node.vx += ((hash(`${node.id}-x`) % 21) - 10) * .02; node.vy += ((hash(`${node.id}-y`) % 21) - 10) * .02; });
    requestDraw();
  }, [requestDraw]);

  useEffect(() => {
    controlsRef.current = { fit, zoomIn: () => zoomBy(1.28), zoomOut: () => zoomBy(.78), reheat };
    return () => { controlsRef.current = null; };
  }, [controlsRef, fit, reheat, zoomBy]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const initialise = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const previous = modelRef.current;
      const nextModel = buildModel(nodes, edges, rect.width, rect.height, previous);
      const topologyChanged = !previous
        || previous.nodes.length !== nextModel.nodes.length
        || previous.hostCount !== nextModel.hostCount
        || previous.nodes.some((node, index) => node.id !== nextModel.nodes[index]?.id);
      const viewportChanged = !previous || previous.width !== nextModel.width || previous.height !== nextModel.height;
      modelRef.current = nextModel;
      if (topologyChanged) userTransformedRef.current = false;
      if (topologyChanged || (viewportChanged && !userTransformedRef.current)) transformRef.current = fitTransform(nextModel);
      requestDraw();
    };
    initialise();
    const observer = new ResizeObserver(initialise);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [edges, nodes, requestDraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const handleWheel = (event) => {
      event.preventDefault();
      const model = modelRef.current;
      if (!model) return;
      const rect = canvas.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerY = event.clientY - rect.top;
      const current = transformRef.current;
      userTransformedRef.current = true;
      const worldX = (pointerX - current.x) / current.k;
      const worldY = (pointerY - current.y) / current.k;
      const k = clamp(current.k * Math.exp(-event.deltaY * .0016), MIN_ZOOM, MAX_ZOOM);
      transformRef.current = { k, x: pointerX - worldX * k, y: pointerY - worldY * k };
      model.transformTween = null;
      model.inertia = { x: 0, y: 0 };
      requestDraw();
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [requestDraw]);

  useEffect(() => {
    requestDraw();
  }, [selectedId, showLabels, linkMode, timelineIds, requestDraw]);

  useEffect(() => () => {
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
  }, []);

  const pointerPosition = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const hitTest = (screenX, screenY) => {
    const model = modelRef.current;
    if (!model) return null;
    const transform = transformRef.current;
    const x = (screenX - transform.x) / transform.k;
    const y = (screenY - transform.y) / transform.k;
    let hit = null;
    let closest = Infinity;
    model.nodes.forEach((node) => {
      if (timelineIdsRef.current && !timelineIdsRef.current.has(node.id)) return;
      const dx = node.x - x;
      const dy = node.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const targetRadius = node.radius / Math.sqrt(transform.k) + 9 / transform.k;
      if (distance <= targetRadius && distance < closest) { hit = node; closest = distance; }
    });
    return hit;
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    const point = pointerPosition(event);
    const hit = hitTest(point.x, point.y);
    event.currentTarget.setPointerCapture(event.pointerId);
    const model = modelRef.current;
    if (model) { model.transformTween = null; model.inertia = { x: 0, y: 0 }; }
    if (hit) {
      hit.fx = hit.x;
      hit.fy = hit.y;
      dragRef.current = { type: 'node', node: hit, startX: point.x, startY: point.y, lastX: point.x, lastY: point.y, lastTime: performance.now(), moved: false };
      canvasRef.current.classList.add('is-dragging-node');
    } else {
      dragRef.current = { type: 'pan', startX: point.x, startY: point.y, lastX: point.x, lastY: point.y, lastTime: performance.now(), moved: false, vx: 0, vy: 0 };
      canvasRef.current.classList.add('is-panning');
    }
  };

  const handlePointerMove = (event) => {
    const point = pointerPosition(event);
    const drag = dragRef.current;
    const model = modelRef.current;
    if (drag && model) {
      const now = performance.now();
      const dx = point.x - drag.lastX;
      const dy = point.y - drag.lastY;
      drag.moved ||= Math.hypot(point.x - drag.startX, point.y - drag.startY) > 4;
      if (drag.type === 'node') {
        const transform = transformRef.current;
        drag.node.fx = (point.x - transform.x) / transform.k;
        drag.node.fy = (point.y - transform.y) / transform.k;
        model.alpha = Math.max(model.alpha, .18);
      } else {
        userTransformedRef.current = true;
        transformRef.current.x += dx;
        transformRef.current.y += dy;
        const elapsed = Math.max(8, now - drag.lastTime);
        drag.vx = (dx / elapsed) * 16;
        drag.vy = (dy / elapsed) * 16;
      }
      drag.lastX = point.x;
      drag.lastY = point.y;
      drag.lastTime = now;
      requestDraw();
      return;
    }
    const hit = hitTest(point.x, point.y);
    const nextId = hit?.id ?? null;
    if (nextId !== hoveredRef.current) {
      hoveredRef.current = nextId;
      setHoveredId(nextId);
      canvasRef.current.classList.toggle('is-over-node', Boolean(hit));
      requestDraw();
    }
  };

  const finishPointer = (event) => {
    const drag = dragRef.current;
    const model = modelRef.current;
    if (!drag) return;
    if (drag.type === 'node') {
      if (!drag.moved) onSelectRef.current({ ...drag.node, kind: drag.node.kind });
      drag.node.fx = null;
      drag.node.fy = null;
      if (model) model.alpha = Math.max(model.alpha, .1);
    } else if (model && drag.moved) {
      model.inertia = { x: drag.vx, y: drag.vy };
    }
    dragRef.current = null;
    canvasRef.current.classList.remove('is-dragging-node', 'is-panning');
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    requestDraw();
  };

  const handleDoubleClick = (event) => {
    const point = pointerPosition(event);
    const node = hitTest(point.x, point.y);
    if (!node) { fit(); return; }
    onSelectRef.current({ ...node, kind: node.kind });
    const model = modelRef.current;
    userTransformedRef.current = true;
    const k = clamp(Math.max(transformRef.current.k, 1.65), MIN_ZOOM, MAX_ZOOM);
    animateTransform({ k, x: model.width / 2 - node.x * k, y: model.height / 2 - node.y * k }, 340);
  };

  const hovered = modelRef.current?.nodeMap.get(hoveredId);
  return (
    <>
      <canvas
        ref={canvasRef}
        className="graph-surface"
        role="img"
        aria-label={`Interactive memory graph with ${nodes.length} nodes. Scroll to zoom, drag the canvas to pan, and drag individual nodes.`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onPointerLeave={() => {
          if (dragRef.current) return;
          hoveredRef.current = null;
          setHoveredId(null);
          canvasRef.current?.classList.remove('is-over-node');
          requestDraw();
        }}
        onDoubleClick={handleDoubleClick}
      />
      <div className={`graph-hover-readout ${hovered ? 'is-visible' : ''}`} aria-live="polite">
        {hovered ? <><span>{hovered.kind}</span><strong>{shortTitle(hovered.title, 52)}</strong><small>{hovered.topic}</small></> : <><span>Explore</span><strong>Hover a node</strong><small>Connections brighten on focus</small></>}
      </div>
      <div
        className="graph-a11y-nodes"
        onFocusCapture={(event) => {
          const nodeId = event.target.dataset.nodeId;
          if (!nodeId) return;
          hoveredRef.current = nodeId;
          setHoveredId(nodeId);
          requestDraw();
        }}
        onBlurCapture={() => {
          hoveredRef.current = null;
          setHoveredId(null);
          requestDraw();
        }}
      >
        {nodes.filter((node) => !timelineIds || timelineIds.has(node.id)).map((node) => <button key={node.id} data-node-id={node.id} onClick={() => onSelect(node)}>{node.kind}: {node.title}</button>)}
      </div>
    </>
  );
}

export function Graph({ data, onSelect, selectedId }) {
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [showLabels, setShowLabels] = useState(false);
  const [linkMode, setLinkMode] = useState('focus');
  const [timelapseOpen, setTimelapseOpen] = useState(false);
  const [timelapsePlaying, setTimelapsePlaying] = useState(false);
  const [timelapseCursor, setTimelapseCursor] = useState(0);
  const [timelapseSpeed, setTimelapseSpeed] = useState(1);
  const controlsRef = useRef(null);
  const playbackRef = useRef({ cursor: 0, at: 0 });
  const visibleNodes = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return data.graph.nodes.filter((node) => (
      (kind === 'all' || node.kind === kind)
      && (status === 'all' || node.status === status)
      && (!normalizedQuery || `${node.title} ${node.project ?? ''} ${node.topic}`.toLowerCase().includes(normalizedQuery))
    ));
  }, [data.graph.nodes, kind, status, query]);
  const visibleIds = useMemo(() => new Set(visibleNodes.map((node) => node.id)), [visibleNodes]);
  const edges = useMemo(() => data.graph.edges.filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target)), [data.graph.edges, visibleIds]);
  const chronologicalNodes = useMemo(() => [...visibleNodes].sort((a, b) => {
    const aTime = Date.parse(a.updatedAt) || 0;
    const bTime = Date.parse(b.updatedAt) || 0;
    return aTime - bTime || a.title.localeCompare(b.title);
  }), [visibleNodes]);
  const timelineIds = useMemo(() => timelapseOpen ? new Set(chronologicalNodes.slice(0, timelapseCursor).map((node) => node.id)) : null, [chronologicalNodes, timelapseCursor, timelapseOpen]);
  const timelineDate = chronologicalNodes[Math.max(0, timelapseCursor - 1)]?.updatedAt;

  useEffect(() => {
    if (!timelapsePlaying || !chronologicalNodes.length) return undefined;
    playbackRef.current = { cursor: timelapseCursor, at: performance.now() };
    let frame = null;
    let lastRendered = timelapseCursor;
    const tick = (now) => {
      const elapsed = (now - playbackRef.current.at) / 1000;
      const nodesPerSecond = Math.max(2, chronologicalNodes.length / 18) * timelapseSpeed;
      const next = Math.min(chronologicalNodes.length, Math.floor(playbackRef.current.cursor + elapsed * nodesPerSecond));
      if (next !== lastRendered) {
        lastRendered = next;
        setTimelapseCursor(next);
      }
      if (next >= chronologicalNodes.length) {
        setTimelapsePlaying(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => { if (frame != null) cancelAnimationFrame(frame); };
  }, [chronologicalNodes.length, timelapsePlaying, timelapseSpeed]);

  useEffect(() => {
    if (!timelapseOpen) return;
    setTimelapseCursor((value) => Math.min(value, chronologicalNodes.length));
  }, [chronologicalNodes.length, timelapseOpen]);

  const startTimelapse = () => {
    const start = timelapseCursor >= chronologicalNodes.length ? 0 : timelapseCursor;
    setTimelapseOpen(true);
    setTimelapseCursor(start);
    setTimelapsePlaying(true);
  };
  const toggleTimelapse = () => {
    if (!timelapseOpen) { startTimelapse(); return; }
    if (!timelapsePlaying && timelapseCursor >= chronologicalNodes.length) setTimelapseCursor(0);
    setTimelapsePlaying((value) => !value);
  };
  const closeTimelapse = () => {
    setTimelapsePlaying(false);
    setTimelapseOpen(false);
    setTimelapseCursor(0);
  };
  const state = data.host?.state;
  if (state && state !== 'ready') return <main className="graph-page page-view"><div className="host-empty"><Icon name="graph" size={30}/><h2>No graph is available for {data.host.label}.</h2><p>This host is {state}.</p></div></main>;
  return (
    <main className="graph-page page-view">
      <header className="graph-heading">
        <div><h1>Memory graph</h1><p>{timelapseOpen ? `${timelapseCursor} of ${visibleNodes.length} nodes revealed` : `${visibleNodes.length} nodes`} · {edges.length} true connections · {linkMode === 'focus' ? 'calm view' : 'all visible'} · {data.host.label}</p></div>
        <label><Icon name="search" size={18}/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search nodes"/></label>
      </header>
      <div className="graph-filters">
        <div>{[['all', 'All'], ['group', 'Task groups'], ['rollout', 'Rollouts'], ['task', 'Tasks'], ['skill', 'Skills']].map(([value, label]) => <button key={value} className={kind === value ? 'is-active' : ''} onClick={() => setKind(value)}>{label}</button>)}</div>
        <div className="graph-filter-actions">
          <button className={timelapseOpen ? 'is-active' : ''} onClick={timelapseOpen ? toggleTimelapse : startTimelapse}><Icon name={timelapsePlaying ? 'pause' : 'play'} size={15}/> {timelapseOpen ? timelapsePlaying ? 'Pause' : 'Resume' : 'Start timelapse'}</button>
          <button className={linkMode === 'focus' ? 'is-active' : ''} onClick={() => setLinkMode((value) => value === 'focus' ? 'all' : 'focus')} title={linkMode === 'focus' ? 'Show every connection' : 'Show the calm contextual view'}><Icon name="graph" size={16}/> {linkMode === 'focus' ? 'Focus links' : 'All links'}</button>
          <button className={showLabels ? 'is-active' : ''} onClick={() => setShowLabels((value) => !value)}><Icon name="document" size={15}/> Labels</button>
          <select value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All statuses</option><option value="success">Success</option><option value="partial">Partial</option><option value="uncertain">Uncertain</option><option value="current">Current</option><option value="planned">Planned</option></select>
        </div>
      </div>
      <div className="graph-canvas">
        <GraphSurface nodes={visibleNodes} edges={edges} selectedId={selectedId} onSelect={onSelect} showLabels={showLabels} linkMode={linkMode} timelineIds={timelineIds} controlsRef={controlsRef}/>
        <div className="graph-instructions"><span>Hover reveals every direct link</span><span>Scroll to zoom</span><span>Drag to explore</span><span>Double-click to focus</span></div>
        {timelapseOpen ? <div className="graph-timelapse" aria-label="Graph time-lapse controls">
          <button className="graph-timelapse__play" onClick={toggleTimelapse} aria-label={timelapsePlaying ? 'Pause time-lapse' : 'Play time-lapse'}><Icon name={timelapsePlaying ? 'pause' : 'play'} size={16}/></button>
          <div className="graph-timelapse__meta"><strong>{timelineDate ? new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(timelineDate)) : 'Before recorded memory'}</strong><span>{timelapseCursor} / {chronologicalNodes.length} memories</span></div>
          <input type="range" min="0" max={chronologicalNodes.length} value={timelapseCursor} onChange={(event) => { setTimelapsePlaying(false); setTimelapseCursor(Number(event.target.value)); }} aria-label="Time-lapse position"/>
          <select value={timelapseSpeed} onChange={(event) => setTimelapseSpeed(Number(event.target.value))} aria-label="Time-lapse speed"><option value="0.5">0.5×</option><option value="1">1×</option><option value="2">2×</option><option value="4">4×</option></select>
          <button onClick={() => { setTimelapsePlaying(false); setTimelapseCursor(0); }} aria-label="Restart time-lapse"><Icon name="restart" size={16}/></button>
          <button onClick={closeTimelapse} aria-label="Close time-lapse"><Icon name="close" size={16}/></button>
        </div> : null}
        <div className="graph-controls">
          <button onClick={() => controlsRef.current?.zoomIn()} aria-label="Zoom in"><Icon name="plus" size={19}/></button>
          <button onClick={() => controlsRef.current?.fit()} aria-label="Fit graph"><Icon name="fit" size={18}/></button>
          <button onClick={() => controlsRef.current?.zoomOut()} aria-label="Zoom out"><Icon name="minus" size={19}/></button>
          <button onClick={() => controlsRef.current?.reheat()} aria-label="Reheat graph layout"><Icon name="refresh" size={18}/></button>
        </div>
        <div className="graph-legend">{Object.entries(KIND_COLORS).map(([itemKind, color]) => <span key={itemKind}><i style={{ background: color }}/>{itemKind === 'group' ? 'Task group' : itemKind}</span>)}<span><b/>Contains / evidence</span><span><b className="is-dashed"/>Related topic</span></div>
        {data.host.id === 'all' ? <div className="graph-host-legend">{data.host.members?.filter((member) => member.state === 'ready').map((member) => <span key={member.id}><i style={{ borderColor: hostColor(member.id) }}/>{member.label}</span>)}</div> : null}
      </div>
    </main>
  );
}
